import type React from 'react';
import { ui } from '../../config/uiSizes';

interface SetupLayoutProps {
  title: string;
  titleAddon?: React.ReactNode;
  topBanner?: React.ReactNode;
  leftContent: React.ReactNode;
  rightContent?: React.ReactNode;
  rightHeader?: React.ReactNode;
  rightBody?: React.ReactNode;
  rightExtraContent?: React.ReactNode;
  rightContentWrapperClass?: string;
  selectedResourceTitle?: string;
  selectedResourceCount?: number;
  buttonSection?: React.ReactNode;
}

export const SetupLayout: React.FC<SetupLayoutProps> = ({
  title,
  titleAddon,
  topBanner,
  leftContent,
  rightContent,
  rightHeader,
  rightBody,
  rightExtraContent,
  rightContentWrapperClass,
  selectedResourceTitle,
  selectedResourceCount,
  buttonSection
}) => {
  return (
    <div className={`${ui.page.padding}`}>
      <div className={`flex flex-col flex-1 rounded-2xl border-1 border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mx-1 mt-0 ${ui.mb.sm} p-1`}>
        <div className="flex flex-col flex-1 rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 p-5">
          {/* Main content card */}
          <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.lgPadding}`}>
            <div className={`flex items-center gap-4 ${ui.mb.section}`}>
              <div className="inline-flex flex-col items-start">
                <h2 className={` ${ui.text.pageTitle} font-extrabold leading-none tracking-tight text-gray-800 dark:text-gray-100`}>
                  {title}
                </h2>
                <div aria-hidden="true" className="mt-2 h-1 w-16 bg-blue-600 dark:bg-blue-500 rounded" />
              </div>
              {titleAddon}
            </div>
            {topBanner && (
              <div className={`${ui.mb.sm}`}>
                {topBanner}
              </div>
            )}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-y-6 ${ui.gap.layout} auto-rows-min`}>
              {/* Left side - Form/Options */}
              <div className={`space-y-6 ${ui.input.layout}`}>
                {leftContent}
              </div>

              {/* Right side - Selected Resources */}
              <div className="space-y-4">
                <div className="flex flex-col items-center">
                  <div className="max-w-2xl w-full mx-auto flex justify-center">
                    <div className={`flex items-center ${ui.gap.md}`}>
                      <h3 className={`${ui.text.sectionTitle} font-extrabold text-gray-800 dark:text-gray-100`}>
                        {selectedResourceTitle}
                      </h3>
                      {typeof selectedResourceCount === 'number' && (
                        <span className={`inline-flex items-center ${ui.badge.sm} rounded-full font-medium bg-blue-600 text-white dark:bg-blue-500 dark:text-gray-100`}>
                          {selectedResourceCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`flex flex-col rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 ${ui.page.cardPadding}`}>
                  <div className="border border-gray-300 dark:border-gray-700 shadow-xl rounded-lg bg-white dark:bg-gray-800 p-4 max-w-2xl">
                    {/* optional header (kept outside the scroller) */}
                    {rightHeader}
                    <div className={`divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto overflow-y-auto scrollbar-stable ${rightContentWrapperClass ?? 'max-h-[420px]'}`}

                      style={{
                        WebkitOverflowScrolling: 'touch',
                        willChange: 'transform',
                        transform: 'translateZ(0)'
                      }}
                    >
                      {rightBody ?? rightContent}
                    </div>
                  </div>
                </div>
                {rightExtraContent && (
                  <div className="w-full pt-3">
                    <div className="max-w-2xl mx-auto w-full">
                      {rightExtraContent}
                    </div>
                  </div>
                )}
              </div>
              {/* Right bottom button section */}
              {buttonSection && (
                <div className="mt-8 align-right lg:col-span-2">
                  <div className="flex justify-end gap-4">
                    {buttonSection}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
